import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { Box, Drawer, Grid, Typography } from "@mui/material";
import { NewsItem, useGitbook } from "api/gitbook";
import { ReactComponent as AnnotationInfoIcon } from "components/Icons/annotation-info.svg";
import { ReactComponent as LinkExternal02Icon } from "components/Icons/link-external-02.svg";
import { ReactComponent as XCloseIcon } from "components/Icons/x-close.svg";
import { ExternalLink } from "ExternalLink";
import { useMessage } from "hooks/intl";
import { OLButton } from "OLButton";
import { useState } from "react";
import { OLDate } from "utilities/dateFormat";
import { useStyles } from "./WhatsNewDrawer.styles";

export function WhatsNewDrawer() {
  const classes = useStyles();
  const { message } = useMessage();

  const { useGitbookPageContent, parseGitbookContent } = useGitbook();
  const { data } = useGitbookPageContent();
  const [open, setOpen] = useState(false);
  const [storedReadNews, setStoredReadNews] = useState<string[]>(
    JSON.parse(localStorage.getItem("readNews") as string),
  );
  const whatsNewContent: NewsItem[] = data && parseGitbookContent(data);

  const areUnreadNews = !!whatsNewContent?.find(
    (variable) => !storedReadNews?.includes(variable.header),
  );

  const markAllAsRead = () => {
    const newReadNews = whatsNewContent?.map((condition) => condition.header);
    localStorage.setItem("readNews", JSON.stringify(newReadNews));
    setStoredReadNews(newReadNews);
  };

  const isNewsRead = (newsHeader: string) =>
    storedReadNews?.find((variable: string) => variable === newsHeader);

  return (
    <>
      <div
        className={`${classes.flexWrapper} ${classes.item}`}
        onClick={() => setOpen(true)}
      >
        <DotLottieReact
          autoplay
          className={`${classes.animatedIcon} ${areUnreadNews ? classes.notificationDot : ""}`}
          loop={areUnreadNews}
          src="https://lottie.host/c4ad2154-4047-4b94-8384-6add6e267bb7/ZmrjL1lWFJ.lottie"
        />
        <span className={`${classes.hiddenOverflow} ${classes.ellipsisText}`}>
          {message({ id: "whatsNew" })}
        </span>
      </div>
      <Drawer
        anchor="right"
        classes={{ paper: classes.drawer }}
        onClose={() => setOpen(false)}
        open={open}
      >
        <div className={classes.header}>
          <Typography className={classes.title}>
            {message({ id: "whatsNew" })}
          </Typography>
          {areUnreadNews && (
            <Typography
              className={classes.markAsRead}
              onClick={() => markAllAsRead()}
            >
              {message({ id: "markAllAsRead" })}
            </Typography>
          )}
          <XCloseIcon
            onClick={() => setOpen(false)}
            className={classes.closeIcon}
          />
        </div>
        <div className={classes.content}>
          {whatsNewContent?.map((attribute, index) => (
            <Grid
              className={`${classes.detailsBox} ${isNewsRead(attribute.header) ? "" : classes.unreadNews}`}
              container
              key={index}
            >
              <AnnotationInfoIcon className={classes.icon} data-role="chips" />
              <div className={classes.newsContent}>
                <div className={classes.newsHeader}>
                  <span className={classes.newsTitle}>{attribute.header}</span>
                  <span className={classes.newsDate}>
                    {OLDate(attribute.date, { formatString: "dd MMM yyyy" })}
                  </span>
                </div>
                <div>
                  {attribute.description}
                  {attribute.link && (
                    <ExternalLink
                      className={classes.descPurple}
                      path={attribute.link}
                    >
                      {message({ id: "learnMore" })}
                    </ExternalLink>
                  )}
                </div>
              </div>
            </Grid>
          ))}
          <Box mt={4} />
          <ExternalLink
            className={classes.descPurple}
            path="https://help.openloyalty.io/whats-new"
          >
            {message({ id: "whatsNewInfo" })}
            <LinkExternal02Icon className={classes.externalIcon} />
          </ExternalLink>
        </div>
        <Box className={classes.btnContainer}>
          <OLButton onClick={() => setOpen(false)} variant="outlined">
            {message({ id: "close" })}
          </OLButton>
        </Box>
      </Drawer>
    </>
  );
}
